// src/features/gallery/components/GenerateMediaPanel.tsx
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SlideOver, {
  classSlideCloseAnimation,
  sliderAnimationDurationInMs,
} from "../../../common/components/SlideOver/SlideOver";
import { closeSlider, setAnimationClassState } from "../../../common/state/slice/modal/modalSlice";
import type { RootState } from "../../../common/state/store";
import { Tabs, Tab, Box } from "@mui/material";
import { getSubscriptionFeature } from "src/features/subscription/state/subscriptionSlice";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getCurrentUser } from "../../profile/state/profileSlice";
import { fetchBlockadeLabsImageModels, getBlockadeLabsModels } from "../state/gallerySlice";
import { GenerateBlockadeLabsImage } from "./GenerateBlockadeLabsImage";
import { CreateAvatar } from "./CreateAvatar";

// ⬇️ keep these exports (unchanged) so other parts of the app and child components can rely on them
export const GENERATE_MEDIA_PANEL_COMPONENT = "generateMediaPanel";
export const FETCH_AI_MODELS_FROM_BLOCKADE_LABS = "fetchAiModelsFromBlockadeLabs";
export const GENERATE_MEDIA_SLIDEOVER_COMPONENT = "generateMediaPanelSlideover";

// ⬇️ NEW: import the extracted component
import GenerateTtsPanel from "./GenerateTtsPanel";

export const GenerateMediaPanel = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useState(0);

  const currentUser = useAppSelector(getCurrentUser);

  const blockadeLabsFeature = useAppSelector((state: RootState) =>
    getSubscriptionFeature(state, "blockade-labs-integration"),
  );

  const isAllowedToUseBlockadeLabsIntegration =
    blockadeLabsFeature?.IsEnabled && currentUser?.role?.name === "Creator";

  const blockadeLabsModels = useAppSelector(getBlockadeLabsModels);

  const showSlider = useAppSelector((state: RootState) => state.modals.showSlider);
  const isGeneratePanelOpen =
    showSlider.show && showSlider.id === GENERATE_MEDIA_SLIDEOVER_COMPONENT;

  const closeSliderAnimation = () => {
    setTimeout(() => {
      dispatch(closeSlider(GENERATE_MEDIA_PANEL_COMPONENT));
    }, sliderAnimationDurationInMs);

    setTimeout(() => {
      dispatch(setAnimationClassState(classSlideCloseAnimation));
    }, 1);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (
      isGeneratePanelOpen &&
      isAllowedToUseBlockadeLabsIntegration &&
      blockadeLabsModels.length === 0
    ) {
      void dispatch(
        fetchBlockadeLabsImageModels({ componentId: FETCH_AI_MODELS_FROM_BLOCKADE_LABS }),
      );
    }
  }, [isGeneratePanelOpen, isAllowedToUseBlockadeLabsIntegration]);

  return (
    <SlideOver large id={GENERATE_MEDIA_SLIDEOVER_COMPONENT}>
      <div className="flex flex-col h-full px-6 pt-8 pb-3 relative">
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label={t("pages.gallery.generateMediaPanel.voiceOver")} />
          {isAllowedToUseBlockadeLabsIntegration && (
            <Tab label={t("pages.gallery.generateMediaPanel.360Image")} />
          )}
          <Tab label={t("general.avatar")} />
        </Tabs>
        <Box className="flex-1 min-h-0 flex flex-col">
          {selectedTab === 0 && <GenerateTtsPanel closeSliderAnimation={closeSliderAnimation} />}

          {selectedTab === 1 && isAllowedToUseBlockadeLabsIntegration && (
            <GenerateBlockadeLabsImage
              models={blockadeLabsModels}
              closeSliderAnimation={closeSliderAnimation}
            />
          )}

          {selectedTab === (isAllowedToUseBlockadeLabsIntegration ? 2 : 1) && (
            <CreateAvatar closeSliderAnimation={closeSliderAnimation} />
          )}
        </Box>
      </div>
    </SlideOver>
  );
};
